import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CITIES, SERVICES, DURATIONS, TIMES, CITY_RATES, PRICED_SERVICE_IDS, TRAINER_RATES_60, MASSAGE_RATES_60 } from "@/lib/catalog";

const METAL_BG =
  "linear-gradient(135deg,#FFFFFF 0%,#E4E6EA 22%,#A6ABB4 55%,#F1F2F4 78%,#898F99 100%)";

const STEP_LABELS = ["Destination", "Dates", "Services", "Preferences", "Details", "Review"];

function makeReference(cityName) {
  const c = (cityName || "WN").replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase() || "WN";
  const d = new Date();
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const rand = Math.floor(Math.random() * 900 + 100);
  return `WN-${c}-${y}${m}${day}-${rand}`;
}

const money = (n) => "$" + Math.round(n).toLocaleString("en-US");
const rangeText = ([lo, hi]) => (lo === hi ? money(lo) : `${money(lo)}–${money(hi)}`);

function gymRange(rates, unit, qty) {
  let [dLo, dHi, wLo, wHi] = rates.gym;
  if (wLo == null) { wLo = dLo * 5; wHi = dHi * 5; }
  if (wHi == null) { wHi = Math.min(dHi * 5, Math.round(wLo * 2.5)); }
  if (wHi < wLo) wHi = wLo;
  let lo, hi;
  if (unit === "day")    { lo = dLo;      hi = dHi; }
  else if (unit === "3day")  { lo = dLo * 3;  hi = dHi * 3; }
  else if (unit === "week")  { lo = wLo;      hi = wHi; }
  else if (unit === "2week") { lo = wLo * 2;  hi = wHi * 2; }
  else                   { lo = wLo * 3.5; hi = wHi * 3.5; }
  return [lo * qty, hi * qty];
}
function coldRange(rates, unit, qty) {
  const [sLo, sHi, wk] = rates.cold;
  let lo, hi;
  if (unit === "single")     { lo = sLo;    hi = sHi; }
  else if (unit === "3pack") { lo = sLo * 3; hi = sHi * 3; }
  else if (unit === "5pack") { lo = sLo * 5; hi = sHi * 5; }
  else { if (wk != null) { lo = wk; hi = wk; } else { lo = sLo * 5; hi = sHi * 5; } }
  return [lo * qty, hi * qty];
}
function trainerRange(cityName, unit, qty) {
  const base = TRAINER_RATES_60[cityName];
  if (!base) return null;
  const mult = unit === "90" ? 1.5 : 1;
  return [base[0] * mult * qty, base[1] * mult * qty];
}
function massageRange(cityName, unit, qty) {
  const base = MASSAGE_RATES_60[cityName];
  if (!base) return null;
  const mult = unit === "90" ? 1.5 : unit === "120" ? 2 : 1;
  return [base[0] * mult * qty, base[1] * mult * qty];
}
function priceForService(sid, rates, unit, qty, cityName) {
  if (sid === "gym"  && rates) return gymRange(rates, unit, qty);
  if (sid === "cold" && rates) return coldRange(rates, unit, qty);
  if (sid === "trainer" && cityName) return trainerRange(cityName, unit, qty);
  if (sid === "massage" && cityName) return massageRange(cityName, unit, qty);
  return null;
}
const emailLooksReal = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(String(v).trim());
const DRAFT_KEY = (email) => `wellnet_sr_draft_v1:${(email || "guest").toLowerCase()}`;

const initialSvc = () => {
  const init = {};
  SERVICES.forEach((s) => (init[s.id] = { on: false, unit: s.units[0].v, qty: 1 }));
  return init;
};

export default function ServiceRequestForm() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [furthest, setFurthest] = useState(0);

  const [cityIdx, setCityIdx] = useState(-1);
  const [areas, setAreas] = useState({});
  const [duration, setDuration] = useState("");
  const [arrival, setArrival] = useState("");
  const [departure, setDeparture] = useState("");
  const [timing, setTiming] = useState({});
  const [svc, setSvc] = useState(initialSvc);
  const [notes, setNotes] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");

  const [gate, setGate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(null);
  const [resumePrompt, setResumePrompt] = useState(null); // draft payload | null
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  useEffect(() => {
    if (user === null) return;
    if (user === false) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (user && typeof user === "object") {
      setName((n) => n || user.name || "");
      setEmail((e) => e || user.email || "");
    }
  }, [user]);

  // Load draft on mount (once user is known)
  useEffect(() => {
    if (draftLoaded || !user || typeof user !== "object") return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY(user.email));
      if (raw) {
        const d = JSON.parse(raw);
        if (d && (d.cityIdx >= 0 || Object.values(d.svc || {}).some((v) => v?.on))) {
          setResumePrompt(d);
        }
      }
    } catch {/* ignore */}
    setDraftLoaded(true);
  }, [user, draftLoaded]);

  // Auto-save draft on any change once loaded
  useEffect(() => {
    if (!draftLoaded || !user || typeof user !== "object" || sent || resumePrompt) return;
    const payload = { step, cityIdx, areas, duration, arrival, departure, timing, svc, notes, name, email, phone, company, savedAt: new Date().toISOString() };
    const hasContent = cityIdx >= 0 || Object.values(svc).some((v) => v.on) || notes || duration || arrival || departure;
    try {
      if (hasContent) {
        localStorage.setItem(DRAFT_KEY(user.email), JSON.stringify(payload));
        setDraftSavedAt(payload.savedAt);
      } else {
        localStorage.removeItem(DRAFT_KEY(user.email));
        setDraftSavedAt(null);
      }
    } catch {/* ignore quota */}
  }, [draftLoaded, user, sent, resumePrompt, step, cityIdx, areas, duration, arrival, departure, timing, svc, notes, name, email, phone, company]);

  const applyDraft = (d) => {
    setStep(d.step || 0);
    setFurthest(d.step || 0);
    setCityIdx(d.cityIdx ?? -1);
    setAreas(d.areas || {});
    setDuration(d.duration || "");
    setArrival(d.arrival || "");
    setDeparture(d.departure || "");
    setTiming(d.timing || {});
    setSvc({ ...initialSvc(), ...(d.svc || {}) });
    setNotes(d.notes || "");
    if (d.name) setName(d.name);
    if (d.email) setEmail(d.email);
    if (d.phone) setPhone(d.phone);
    if (d.company) setCompany(d.company);
    setResumePrompt(null);
  };

  const discardDraft = () => {
    try { if (user?.email) localStorage.removeItem(DRAFT_KEY(user.email)); } catch {/* ignore */}
    setResumePrompt(null);
  };

  const city = cityIdx >= 0 ? CITIES[cityIdx] : null;
  const rates = city ? CITY_RATES[city.name] : null;
  const selectedAreas = useMemo(() => Object.keys(areas), [areas]);
  const selectedTiming = useMemo(() => Object.keys(timing), [timing]);
  const selectedServices = useMemo(() => SERVICES.filter((s) => svc[s.id]?.on), [svc]);

  const stepValid = useCallback((i) => {
    if (i === 0) return cityIdx >= 0;
    if (i === 2) return selectedServices.length > 0;
    if (i === 4) return !!name.trim() && emailLooksReal(email);
    return true;
  }, [cityIdx, selectedServices.length, name, email]);
  const canReach = useCallback((i) => {
    for (let k = 0; k < i; k++) if (!stepValid(k)) return false;
    return true;
  }, [stepValid]);
  const stepGate = (i) => {
    if (i === 0) return "Choose a city to continue.";
    if (i === 2) return "Select at least one service to continue.";
    if (i === 4) {
      if (!name.trim() && !emailLooksReal(email)) return "Add your name and email so we can reach you.";
      if (!name.trim()) return "Add your name so we can reach you.";
      return "Add a valid email address so we can reach you.";
    }
    return "";
  };

  const goStep = (i) => {
    if (i < 0 || i >= STEP_LABELS.length || !canReach(i)) return;
    setStep(i);
    setGate("");
    setFurthest((f) => Math.max(f, i));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const summary = useMemo(() => {
    let lo = 0, hi = 0, quoted = 0;
    const rows = selectedServices.map((s) => {
      const st = svc[s.id];
      const unitObj = s.units.find((u) => u.v === st.unit);
      const detail = `${unitObj?.l || st.unit} × ${st.qty}`;
      if (PRICED_SERVICE_IDS.has(s.id) && (rates || city)) {
        const r = priceForService(s.id, rates, st.unit, st.qty, city?.name);
        if (r) { lo += r[0]; hi += r[1]; return { name: s.name, detail, val: rangeText(r), cls: "" }; }
      }
      if (PRICED_SERVICE_IDS.has(s.id) && !city) {
        return { name: s.name, detail, val: "select a city", cls: "q" };
      }
      quoted++;
      return { name: s.name, detail, val: "quoted", cls: "q" };
    });
    const totalTxt = hi > 0 ? rangeText([lo, hi]) : "—";
    return { rows, lo, hi, quoted, totalTxt };
  }, [selectedServices, svc, rates]);

  const submit = async () => {
    setError("");
    const reference = makeReference(city.name);
    const service_details = selectedServices.map((s) => {
      const st = svc[s.id];
      const unitObj = s.units.find((u) => u.v === st.unit);
      return { name: s.name, unit: st.unit, unit_label: unitObj?.l || st.unit, qty: st.qty };
    });
    setSubmitting(true);
    try {
      const res = await api.post("/service-requests", {
        city: city.name,
        services: selectedServices.map((s) => s.name),
        service_details,
        areas: selectedAreas,
        duration: duration || null,
        arrival: arrival || null,
        departure: departure || null,
        timing: selectedTiming,
        notes: notes || null,
        contact_phone: phone || null,
        contact_company: company || null,
        reference,
      });
      setSent({ reference: res.data?.reference || reference });
      try { if (user?.email) localStorage.removeItem(DRAFT_KEY(user.email)); } catch {/* ignore */}
      setDraftSavedAt(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user === false) return null;

  const summaryWhere = city
    ? `${city.name}${duration ? " · " + duration : ""}${selectedAreas.length ? ` · ${selectedAreas.length} area${selectedAreas.length > 1 ? "s" : ""}` : ""}`
    : "No city selected yet";

  return (
    <div
      data-testid="service-request-page"
      className="min-h-screen text-neutral-200"
      style={{
        background: "radial-gradient(ellipse at 50% -10%, #202226 0%, #0e0f12 45%, #050506 85%, #000000 100%)",
        fontFamily: "'Archivo', system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      {/* Masthead */}
      <header className="sticky top-0 z-30 backdrop-blur-md" style={{ borderBottom: "1px solid #22252A", background: "rgba(0,0,0,0.72)" }}>
        <div className="mx-auto max-w-6xl px-5 py-4 flex items-center gap-4">
          <Link to="/member" className="flex items-center gap-3 min-w-0">
            <div>
              <div
                aria-hidden
                className="w-40 h-4"
                style={{
                  background: METAL_BG,
                  WebkitMaskImage: "url(/wellnet-logo.png)", maskImage: "url(/wellnet-logo.png)",
                  WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                  WebkitMaskSize: "contain", maskSize: "contain",
                  WebkitMaskPosition: "left center", maskPosition: "left center",
                }}
              />
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mt-1">Concierge Request</div>
            </div>
          </Link>
          <div className="ml-auto flex items-center gap-4 text-[10px] uppercase tracking-[0.25em]">
            {draftSavedAt && !sent && (
              <span data-testid="draft-saved-badge" className="text-neutral-500 hidden sm:inline">Draft saved</span>
            )}
            <span data-testid="member-email" className="text-neutral-400 hidden sm:inline">{user.email}</span>
            <button
              data-testid="member-logout-btn"
              onClick={async () => { await logout(); navigate("/"); }}
              className="border border-neutral-600 text-neutral-300 px-3 py-1.5 hover:bg-neutral-200 hover:text-black transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Step bar */}
      {!sent && (
        <div style={{ background: "#000", borderBottom: "1px solid #22252A" }}>
          <div className="mx-auto max-w-6xl px-5">
            <div className="flex gap-1 overflow-x-auto py-2.5" style={{ scrollbarWidth: "none" }} data-testid="stepbar">
              {STEP_LABELS.map((label, i) => {
                const isCurrent = i === step;
                const isDone = i < step && stepValid(i);
                const reachable = canReach(i);
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid={`step-${i}`}
                    aria-current={isCurrent ? "step" : undefined}
                    disabled={!reachable}
                    onClick={() => goStep(i)}
                    className="flex items-center gap-2 flex-none rounded-full px-3.5 py-1.5 text-[13.5px] font-semibold whitespace-nowrap transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
                    style={{
                      color: isCurrent ? "#0A0B0D" : isDone ? "#C7CAD1" : "#7C818A",
                      background: isCurrent ? "#F3F4F6" : "transparent",
                    }}
                  >
                    <span
                      className="grid place-items-center rounded-full text-[11px] font-bold"
                      style={{
                        width: 21, height: 21,
                        border: `1.5px solid ${isDone ? "#7FC79A" : "currentColor"}`,
                        color: isDone ? "#7FC79A" : (isCurrent ? "#0A0B0D" : "currentColor"),
                      }}
                    >
                      {isDone ? "✓" : i + 1}
                    </span>
                    {label}
                  </button>
                );
              })}
            </div>
            <div style={{ height: 2, background: "#22252A" }}>
              <div style={{ height: 2, width: `${Math.round((step / (STEP_LABELS.length - 1)) * 100)}%`, background: METAL_BG, transition: "width .25s ease" }} />
            </div>
          </div>
        </div>
      )}

      {sent ? (
        <SentScreen reference={sent.reference} email={email} onNew={() => {
          setSent(null); setStep(0); setFurthest(0);
          setSvc(initialSvc());
          setCityIdx(-1); setAreas({}); setDuration(""); setArrival(""); setDeparture(""); setTiming({}); setNotes("");
        }} />
      ) : (
        <main className="mx-auto max-w-6xl px-5 pt-6 pb-32">
          <Link
            to="/member"
            data-testid="sr-page-back"
            className="inline-flex items-center gap-2 mb-5 px-4 py-2 text-[11px] uppercase tracking-[0.3em] text-neutral-300 hover:text-neutral-100 hover:border-neutral-200 transition-colors"
            style={{ border: "1px solid #31353C", borderRadius: 8 }}
          >
            <span aria-hidden>←</span> Back
          </Link>
          {resumePrompt && (
            <div
              data-testid="resume-banner"
              className="mb-4 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              style={{ background: "#101114", border: "1px solid #C7CAD1" }}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#C7CAD1] mb-1">Draft found</div>
                <div className="text-[13.5px] text-neutral-300">
                  You started a request on {resumePrompt.savedAt ? new Date(resumePrompt.savedAt).toLocaleString() : "an earlier visit"}. Pick up where you left off?
                </div>
              </div>
              <div className="flex gap-2 flex-none">
                <button
                  data-testid="draft-discard-btn"
                  type="button"
                  onClick={discardDraft}
                  className="rounded-lg py-2 px-4 font-semibold text-[13px]"
                  style={{ background: "transparent", color: "#C7CAD1", border: "1px solid #31353C" }}
                >
                  Start fresh
                </button>
                <button
                  data-testid="draft-resume-btn"
                  type="button"
                  onClick={() => applyDraft(resumePrompt)}
                  className="rounded-lg py-2 px-4 font-bold text-[13px]"
                  style={{ background: METAL_BG, color: "#08090B" }}
                >
                  Resume draft
                </button>
              </div>
            </div>
          )}
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_330px] items-start">
            {/* LEFT — active step pane */}
            <section className="rounded-xl overflow-hidden min-w-0" style={{ background: "#101114", border: "1px solid #22252A" }}>
              {step === 0 && (
                <PaneHeader eyebrow="Step 01" title="Where are you headed?"
                  intro="Pick the city and, if you like, the neighbourhoods you want to stay close to." />
              )}
              {step === 1 && (
                <PaneHeader eyebrow="Step 02" title="When is the trip?"
                  intro="Give us a rough length and, ideally, arrival and departure dates." />
              )}
              {step === 2 && (
                <PaneHeader eyebrow="Step 03" title="Which services do you want lined up?"
                  intro="Select any — set the length or package and how many of each." />
              )}
              {step === 3 && (
                <PaneHeader eyebrow="Step 04" title="Any scheduling preferences?"
                  intro="Optional — tell us when you like to train, and anything else we should know." />
              )}
              {step === 4 && (
                <PaneHeader eyebrow="Step 05" title="How can we reach you?"
                  intro="We use these to confirm bookings and send your itinerary." />
              )}
              {step === 5 && (
                <PaneHeader eyebrow="Step 06" title="Review your request"
                  intro="Have a last look. When you're happy, send it to the WELLNET concierge team." />
              )}

              <div className="px-5 sm:px-6 pb-4 pt-4 flex flex-col gap-4">
                {step === 0 && (
                  <>
                    <Field label="City">
                      <select
                        data-testid="city-select"
                        value={cityIdx}
                        onChange={(e) => { setCityIdx(parseInt(e.target.value, 10)); setAreas({}); }}
                        className="silver-input"
                      >
                        <option value={-1}>Select a city…</option>
                        {CITIES.map((c, i) => <option key={c.name} value={i}>{c.name}</option>)}
                      </select>
                    </Field>
                    {city && (
                      <Field label={<>Preferred areas <span className="normal-case tracking-normal font-normal text-neutral-500">— optional</span></>}>
                        <Chips testid="area-chips" items={city.areas}
                          isOn={(a) => !!areas[a]}
                          onToggle={(a) => setAreas((p) => { const n = { ...p }; n[a] ? delete n[a] : (n[a] = true); return n; })} />
                      </Field>
                    )}
                  </>
                )}

                {step === 1 && (
                  <>
                    <Field label="Length of stay">
                      <Chips testid="dur-chips" items={DURATIONS}
                        isOn={(d) => duration === d}
                        onToggle={(d) => setDuration((cur) => (cur === d ? "" : d))} />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <Field label="Arrival">
                        <input data-testid="sr-arrival" type="date" value={arrival} onChange={(e) => setArrival(e.target.value)} className="silver-input" />
                      </Field>
                      <Field label="Departure">
                        <input data-testid="sr-departure" type="date" value={departure} onChange={(e) => setDeparture(e.target.value)} className="silver-input" />
                      </Field>
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <div>
                      {SERVICES.map((s) => {
                        const st = svc[s.id];
                        return (
                          <ServiceRow key={s.id} service={s} state={st} rates={rates} cityName={city?.name}
                            onToggle={(on) => setSvc((p) => ({ ...p, [s.id]: { ...p[s.id], on } }))}
                            onUnit={(v) => setSvc((p) => ({ ...p, [s.id]: { ...p[s.id], unit: v } }))}
                            onQty={(delta) => setSvc((p) => ({ ...p, [s.id]: { ...p[s.id], qty: Math.max(1, Math.min(20, p[s.id].qty + delta)) } }))} />
                        );
                      })}
                    </div>
                    {city && (
                      <details className="rounded-lg overflow-hidden" style={{ border: "1px solid #22252A", background: "#0B0C0F" }}>
                        <summary className="cursor-pointer px-3.5 py-2.5 text-[13px] font-semibold text-neutral-400 select-none flex items-center gap-2">
                          <span className="w-3 text-[#C7CAD1] font-bold">+</span>
                          Sample partners in {city.name}
                        </summary>
                        <ul className="px-3.5 pb-3 pt-1 pl-8 flex flex-col gap-1">
                          {city.partners.gym.map((p, i) => (
                            <li key={"g" + i} className="text-[13px] text-neutral-400">
                              <b className="text-[#C7CAD1] font-semibold text-[10.5px] tracking-[0.1em] uppercase mr-2">Gym</b>{p}
                            </li>
                          ))}
                          {city.partners.recovery.map((p, i) => (
                            <li key={"r" + i} className="text-[13px] text-neutral-400">
                              <b className="text-[#C7CAD1] font-semibold text-[10.5px] tracking-[0.1em] uppercase mr-2">Recovery</b>{p}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                )}

                {step === 3 && (
                  <>
                    <Field label="Scheduling">
                      <Chips testid="time-chips" items={TIMES}
                        isOn={(t) => !!timing[t]}
                        onToggle={(t) => setTiming((p) => { const n = { ...p }; n[t] ? delete n[t] : (n[t] = true); return n; })} />
                    </Field>
                    <Field label="Anything we should know">
                      <textarea data-testid="sr-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder="Injuries or conditions, therapist preference, training focus, equipment you need, hotel or residence address, travel companions…"
                        rows={4} className="silver-input resize-y min-h-[96px]" />
                    </Field>
                  </>
                )}

                {step === 4 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <Field label="Full name">
                      <input data-testid="sr-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Ellis" className="silver-input" />
                    </Field>
                    <Field label="Email">
                      <input data-testid="sr-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jordan@company.com" className="silver-input" />
                    </Field>
                    <Field label="Mobile">
                      <input data-testid="sr-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 010-2233" className="silver-input" />
                    </Field>
                    <Field label={<>Company or member ID <span className="normal-case tracking-normal font-normal text-neutral-500">— optional</span></>}>
                      <input data-testid="sr-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Optional" className="silver-input" />
                    </Field>
                  </div>
                )}

                {step === 5 && (
                  <ReviewPane
                    city={city} areas={selectedAreas} duration={duration} arrival={arrival} departure={departure}
                    timing={selectedTiming} notes={notes} name={name} email={email} phone={phone}
                    rows={summary.rows} onEdit={goStep} onSubmit={submit} submitting={submitting} error={error}
                  />
                )}
              </div>

              {/* Nav bar */}
              {step < 5 && (
                <div className="flex items-center gap-3 px-5 sm:px-6 py-4 flex-wrap" style={{ borderTop: "1px solid #22252A" }}>
                  <button
                    data-testid="back-btn"
                    type="button"
                    disabled={step === 0}
                    onClick={() => goStep(step - 1)}
                    className="rounded-lg py-2.5 px-5 font-semibold text-[14px] disabled:opacity-45 disabled:cursor-not-allowed"
                    style={{ background: "transparent", color: "#C7CAD1", border: "1px solid #31353C" }}
                  >
                    Back
                  </button>
                  <span className="mx-auto text-[12px] uppercase tracking-[0.06em] font-semibold text-neutral-500 tabular-nums">
                    Step {step + 1} of {STEP_LABELS.length}
                  </span>
                  <button
                    data-testid="next-btn"
                    type="button"
                    onClick={() => { if (!stepValid(step)) { setGate(stepGate(step)); return; } goStep(step + 1); }}
                    className="rounded-lg py-2.5 px-5 font-bold text-[14.5px] disabled:cursor-not-allowed"
                    style={{
                      background: stepValid(step) ? METAL_BG : "#171A1E",
                      color: stepValid(step) ? "#08090B" : "#7C818A",
                      border: stepValid(step) ? "1px solid transparent" : "1px solid #22252A",
                    }}
                  >
                    {step === 4 ? "Review request" : "Continue"}
                  </button>
                  {gate && !stepValid(step) && (
                    <div className="basis-full text-[12.5px] font-semibold" style={{ color: "#D9A05B" }}>{gate}</div>
                  )}
                </div>
              )}
            </section>

            {/* RIGHT — summary rail */}
            <aside className="lg:sticky lg:top-32 flex flex-col gap-3.5">
              <div className="rounded-xl overflow-hidden" style={{ background: "#101114", border: "1px solid #31353C" }}>
                <div className="px-4 pt-4 pb-3 border-b" style={{ background: "#000", borderColor: "#22252A" }}>
                  <h2 className="text-[17px] font-semibold" style={{ fontFamily: "'Syne','Archivo',sans-serif" }}>Your request</h2>
                  <div data-testid="sum-where" className="text-[13px] text-neutral-400 mt-1">{summaryWhere}</div>
                </div>
                <div className="px-4 py-1 max-h-[340px] overflow-y-auto">
                  {summary.rows.length === 0 ? (
                    <div className="text-center text-neutral-500 text-[13.5px] py-6">
                      Pick a city, then choose the services you want lined up.
                    </div>
                  ) : summary.rows.map((r, i) => (
                    <div key={i} className="flex gap-2 py-2 border-b last:border-b-0" style={{ borderColor: "#22252A" }}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[13.5px]">{r.name}</div>
                        <div className="text-[12.5px] text-neutral-500">{r.detail}</div>
                      </div>
                      <div className={`text-[13px] tabular-nums self-center whitespace-nowrap ${r.cls === "q" ? "text-[10.5px] font-bold tracking-[0.1em] uppercase text-neutral-500" : "text-[#C7CAD1]"}`}>
                        {r.val}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-3.5 border-t" style={{ borderColor: "#22252A", background: "#0B0C0F" }}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10.5px] font-semibold tracking-[0.14em] uppercase text-neutral-500">Indicative</span>
                    <span
                      data-testid="sum-total"
                      className="ml-auto font-bold tabular-nums"
                      style={{
                        fontFamily: "'Syne','Archivo',sans-serif", fontSize: 22,
                        background: METAL_BG, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
                      }}
                    >
                      {summary.totalTxt}
                    </span>
                  </div>
                  <p className="text-[11.5px] text-neutral-500 mt-2 leading-[1.5]">
                    Ranges come from partner day and week rates published at time of research.
                    {summary.quoted > 0 && ` ${summary.quoted} service${summary.quoted > 1 ? "s are" : " is"} quoted once we match your provider.`}
                    {" "}Concierge fee and any booking minimums are confirmed separately.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </main>
      )}

      {/* Mobile bottom bar */}
      {!sent && step < 5 && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 flex items-center gap-3 px-5 py-3"
          style={{ background: "#000", borderTop: "1px solid #31353C", paddingBottom: "calc(12px + env(safe-area-inset-bottom,0px))" }}>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] uppercase tracking-[0.06em] font-semibold text-neutral-500">
              {summary.rows.length ? `${summary.rows.length} service${summary.rows.length > 1 ? "s" : ""} selected` : "Nothing selected"}
            </div>
            <div className="font-bold tabular-nums"
              style={{ fontFamily: "'Syne','Archivo',sans-serif", fontSize: 18, background: METAL_BG, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
              {summary.totalTxt}
            </div>
          </div>
          <button
            data-testid="next-btn-mobile"
            type="button"
            onClick={() => { if (!stepValid(step)) { setGate(stepGate(step)); return; } goStep(step + 1); }}
            className="rounded-lg py-2.5 px-5 font-bold text-[14px]"
            style={{
              background: stepValid(step) ? METAL_BG : "#171A1E",
              color: stepValid(step) ? "#08090B" : "#7C818A",
            }}
          >
            {step === 4 ? "Review" : "Continue"}
          </button>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700;800&family=Archivo:wght@400;500;600;700&display=swap');
        .silver-input {
          width: 100%;
          background: #0B0C0F;
          border: 1px solid #22252A;
          color: #F3F4F6;
          border-radius: 9px;
          padding: 10px 12px;
          color-scheme: dark;
          transition: border-color .12s;
          font: inherit;
        }
        .silver-input:hover { border-color: #31353C; }
        .silver-input:focus-visible { outline: 2px solid #C7CAD1; outline-offset: 1px; border-color: transparent; }
        .silver-input::placeholder { color: #7C818A; }
        select.silver-input {
          appearance: none;
          background-image:
            linear-gradient(45deg, transparent 50%, #898F99 50%),
            linear-gradient(135deg, #898F99 50%, transparent 50%);
          background-position: calc(100% - 18px) 51%, calc(100% - 13px) 51%;
          background-size: 5px 5px, 5px 5px;
          background-repeat: no-repeat;
          padding-right: 38px;
        }
      `}</style>
    </div>
  );
}

/* -------- Subcomponents -------- */

function PaneHeader({ eyebrow, title, intro }) {
  return (
    <div className="px-5 sm:px-6 pt-5">
      <div className="text-[10.5px] font-bold tracking-[0.16em] uppercase text-neutral-500">{eyebrow}</div>
      <h2 className="mt-2 text-[23px] font-semibold tracking-[-0.01em]" style={{ fontFamily: "'Syne','Archivo',sans-serif" }}>{title}</h2>
      {intro && <p className="mt-2 text-[14px] text-neutral-400 max-w-[58ch]">{intro}</p>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5 min-w-0">
      <span className="text-[11px] font-semibold tracking-[0.13em] uppercase text-neutral-500">{label}</span>
      {children}
    </label>
  );
}

function Chips({ items, isOn, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {items.map((label) => {
        const on = isOn(label);
        return (
          <button
            key={label}
            type="button"
            data-testid={`chip-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            aria-pressed={on ? "true" : "false"}
            onClick={() => onToggle(label)}
            className="whitespace-nowrap transition-colors"
            style={{
              border: "1px solid " + (on ? "#F3F4F6" : "#31353C"),
              background: on ? "#F3F4F6" : "#0B0C0F",
              color: on ? "#0A0B0D" : "#A6ABB4",
              borderRadius: 999, padding: "6px 13px", fontSize: 13.5,
              fontWeight: on ? 600 : 500, cursor: "pointer",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function ServiceRow({ service, state, rates, cityName, onToggle, onUnit, onQty }) {
  const on = state.on;
  const isPriced = PRICED_SERVICE_IDS.has(service.id);
  let estText = "";
  if (on) {
    if (isPriced) {
      const r = priceForService(service.id, rates, state.unit, state.qty, cityName);
      if (r) estText = "approx. " + rangeText(r);
      else estText = "select a city for rates";
    } else {
      estText = "quoted after matching";
    }
  }
  return (
    <div
      data-testid={`svc-${service.id}`}
      className="transition-all"
      style={{
        borderTop: "1px solid #22252A",
        padding: on ? "12px 18px" : "14px 0",
        marginInline: on ? -18 : 0,
        background: on ? "#171A1E" : "transparent",
        borderRadius: on ? 8 : 0,
        boxShadow: on ? "inset 2px 0 0 #C7CAD1" : "none",
      }}
    >
      <div className="flex items-start gap-3">
        <button
          type="button" role="checkbox" aria-checked={on}
          data-testid={`chk-${service.id}`}
          onClick={() => onToggle(!on)}
          className="flex-none mt-0.5 grid place-items-center transition-all"
          style={{
            width: 20, height: 20,
            border: "1.5px solid " + (on ? "#F3F4F6" : "#31353C"),
            borderRadius: 6,
            background: on ? "#F3F4F6" : "#0B0C0F",
            cursor: "pointer",
          }}
        >
          {on && (
            <span aria-hidden style={{ width: 10, height: 6, borderLeft: "2px solid #0A0B0D", borderBottom: "2px solid #0A0B0D", transform: "rotate(-45deg) translate(1px,-1px)" }} />
          )}
        </button>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onToggle(!on)}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[15px]">{service.name}</span>
            <span
              className="text-[10px] font-bold tracking-[0.11em] uppercase px-2 py-0.5 rounded whitespace-nowrap"
              style={
                isPriced
                  ? { background: "#C7CAD1", color: "#0A0B0D" }
                  : { border: "1px solid #31353C", color: "#898F99" }
              }
            >
              {isPriced ? "indicative rate" : "quoted"}
            </span>
          </div>
          <div className="text-[13px] text-neutral-500 mt-0.5">{service.desc}</div>
        </div>
      </div>

      {on && (
        <div className="flex flex-wrap items-end gap-2.5 mt-3 pl-8">
          <label className="flex flex-col gap-1 min-w-0">
            <span className="text-[10.5px] font-semibold tracking-[0.11em] uppercase text-neutral-500">{service.unitLabel}</span>
            <select data-testid={`unit-${service.id}`} value={state.unit} onChange={(e) => onUnit(e.target.value)} className="silver-input" style={{ minWidth: 168 }}>
              {service.units.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10.5px] font-semibold tracking-[0.11em] uppercase text-neutral-500">{service.qtyLabel}</span>
            <div className="flex items-center overflow-hidden" style={{ border: "1px solid #31353C", borderRadius: 9, background: "#0B0C0F", width: "fit-content" }}>
              <button type="button" aria-label="Decrease" data-testid={`dec-${service.id}`} onClick={() => onQty(-1)}
                className="w-9 h-[38px] text-[17px] text-neutral-400 hover:bg-neutral-800 hover:text-white transition-colors">−</button>
              <span data-testid={`qty-${service.id}`}
                className="w-10 h-[38px] leading-[38px] text-center font-semibold tabular-nums"
                style={{ borderLeft: "1px solid #31353C", borderRight: "1px solid #31353C" }}>
                {state.qty}
              </span>
              <button type="button" aria-label="Increase" data-testid={`inc-${service.id}`} onClick={() => onQty(1)}
                className="w-9 h-[38px] text-[17px] text-neutral-400 hover:bg-neutral-800 hover:text-white transition-colors">+</button>
            </div>
          </label>
          {estText && (
            <span className="ml-auto text-[13px] text-[#A6ABB4] tabular-nums pb-2 whitespace-nowrap">{estText}</span>
          )}
        </div>
      )}
    </div>
  );
}

function ReviewPane({ city, areas, duration, arrival, departure, timing, notes, name, email, phone, rows, onEdit, onSubmit, submitting, error }) {
  const rowStyle = { borderBottom: "1px solid #22252A" };
  const svcHtml = rows.map((r, i) => (
    <div key={i} className="text-[13.5px]">
      {r.name}{" "}
      <span className="text-neutral-500 text-[12.5px]">{r.detail} · {r.cls === "q" ? "quoted" : r.val}</span>
    </div>
  ));
  return (
    <div className="flex flex-col gap-3.5">
      <div className="rounded-lg overflow-hidden" style={{ border: "1px solid #22252A" }}>
        <RecapRow label="Destination" onEdit={() => onEdit(0)}>
          {city?.name}
          <div className="text-neutral-500 text-[12.5px]">{areas.length ? areas.join(", ") : "No area preference"}</div>
        </RecapRow>
        <RecapRow label="Dates" onEdit={() => onEdit(1)}>
          {duration || "Not specified"}
          <div className="text-neutral-500 text-[12.5px]">
            {arrival || "no arrival date"} → {departure || "no departure date"}
          </div>
        </RecapRow>
        <RecapRow label="Services" onEdit={() => onEdit(2)}>
          {svcHtml}
        </RecapRow>
        <RecapRow label="Preferences" onEdit={() => onEdit(3)} style={rowStyle}>
          {timing.length ? timing.join(", ") : "No timing preference"}
          {notes && <div className="text-neutral-500 text-[12.5px] whitespace-pre-wrap mt-1">{notes}</div>}
        </RecapRow>
        <RecapRow label="Member" onEdit={() => onEdit(4)} last>
          {name}
          <div className="text-neutral-500 text-[12.5px]">{email}{phone && ` · ${phone}`}</div>
        </RecapRow>
      </div>

      {error && (
        <div className="text-[13px] font-semibold px-3 py-2" style={{ color: "#D9A05B", border: "1px solid #D9A05B", borderRadius: 8 }}>
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="back-btn"
          type="button"
          onClick={() => onEdit(4)}
          className="rounded-lg py-2.5 px-5 font-semibold text-[14px]"
          style={{ background: "transparent", color: "#C7CAD1", border: "1px solid #31353C" }}
        >
          Back
        </button>
        <button
          data-testid="sr-submit-btn"
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className="rounded-lg py-2.5 px-6 font-bold text-[14.5px] disabled:opacity-60 disabled:cursor-not-allowed ml-auto"
          style={{ background: METAL_BG, color: "#08090B" }}
        >
          {submitting ? "Sending…" : "Send to WELLNET"}
        </button>
      </div>
      <div className="text-[12.5px] text-neutral-500 px-3 py-2.5 rounded-lg" style={{ border: "1px solid #31353C", background: "#000" }}>
        Goes to <span className="text-[#C7CAD1] font-semibold">bg@wellnet.co</span> — you'll receive a confirmation with your reference number.
      </div>
    </div>
  );
}

function RecapRow({ label, children, onEdit, last }) {
  return (
    <div className="flex gap-3 px-4 py-3" style={{ borderBottom: last ? "none" : "1px solid #22252A" }}>
      <dt className="w-[100px] flex-none text-[11px] font-semibold tracking-[0.11em] uppercase text-neutral-500 pt-0.5">{label}</dt>
      <dd className="flex-1 min-w-0 text-neutral-100 text-[13.5px]">{children}</dd>
      <button type="button" onClick={onEdit} className="text-[12px] text-neutral-500 hover:text-neutral-200 underline underline-offset-[3px] self-start">Edit</button>
    </div>
  );
}

function SentScreen({ reference, email, onNew }) {
  return (
    <main className="mx-auto max-w-3xl px-5 py-16" data-testid="request-success">
      <div className="rounded-xl overflow-hidden" style={{ background: "#101114", border: "1px solid #7FC79A" }}>
        <div className="flex items-start gap-4 px-5 py-6" style={{ borderBottom: "1px solid #22252A" }}>
          <div aria-hidden className="flex-none rounded-full relative" style={{ width: 34, height: 34, border: "1.5px solid #7FC79A" }}>
            <span className="absolute" style={{ left: 11, top: 8, width: 9, height: 15, borderRight: "2px solid #7FC79A", borderBottom: "2px solid #7FC79A", transform: "rotate(42deg)" }} />
          </div>
          <div>
            <div className="font-semibold text-[18px]" style={{ color: "#7FC79A", fontFamily: "'Syne','Archivo',sans-serif" }}>Request sent</div>
            <div className="text-[13.5px] text-neutral-400 mt-1 max-w-[56ch]">
              It's with the WELLNET concierge team under reference{" "}
              <b data-testid="sr-reference" className="text-[#C7CAD1] tabular-nums">{reference}</b>.
              We reply within one business day with confirmed venues, times and a firm quote —
              watch for it at <span className="text-[#C7CAD1]">{email}</span>.
            </div>
          </div>
        </div>
        <div className="px-5 py-4 flex flex-wrap gap-3">
          <button data-testid="new-request-btn" type="button" onClick={onNew}
            className="rounded-lg py-2.5 px-5 font-semibold text-[14px]"
            style={{ background: "transparent", color: "#C7CAD1", border: "1px solid #31353C" }}>
            New request
          </button>
          <Link to="/member/history" data-testid="history-btn"
            className="rounded-lg py-2.5 px-5 font-bold text-[14px] inline-flex items-center justify-center"
            style={{ background: METAL_BG, color: "#08090B" }}>
            View request history
          </Link>
        </div>
      </div>
    </main>
  );
}
